import { useCallback, useEffect, useRef } from "react";
import { Bot, Calendar, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useStreamText } from "@/hooks/use-stream-text";
import { FormatCopilotText } from "@/components/shared/format-copilot-text";

export interface CopilotChatMessage {
  id: string;
  sender: "user" | "copilot";
  text: string;
  timestamp: string;
  appointments?: Array<{ time: string; patient_name: string; reason: string; status: string }>;
  /** Animate copilot reply with typewriter effect */
  stream?: boolean;
}

function TypingIndicator() {
  return (
    <div className="copilot-msg-in flex justify-start gap-2">
      <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary ring-2 ring-primary/20">
        <Bot className="h-4 w-4 animate-pulse" />
      </div>
      <div className="max-w-[85%] rounded-xl border border-primary/25 bg-primary/5 p-3.5 shadow-sm backdrop-blur-sm dark:border-primary/20 dark:bg-primary/10">
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1" aria-hidden>
            <span className="copilot-typing-dot h-2 w-2" />
            <span className="copilot-typing-dot h-2 w-2 [animation-delay:150ms]" />
            <span className="copilot-typing-dot h-2 w-2 [animation-delay:300ms]" />
          </div>
          <span className="text-xs font-medium text-foreground">Thinking…</span>
        </div>
      </div>
    </div>
  );
}

function CopilotReplyBubble({
  message,
  onStreamComplete,
  onContentUpdate,
}: {
  message: CopilotChatMessage;
  onStreamComplete?: (id: string) => void;
  onContentUpdate?: () => void;
}) {
  const streamed = useStreamText(
    message.text,
    !!message.stream,
    message.stream ? () => onStreamComplete?.(message.id) : undefined,
    24,
  );
  const showCaret = message.stream && streamed.length < message.text.length;

  useEffect(() => {
    if (message.stream) onContentUpdate?.();
  }, [streamed, message.stream, onContentUpdate]);

  return (
    <div className="copilot-msg-in flex justify-start gap-2">
      <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Bot className="h-3.5 w-3.5" />
      </div>
      <div className="max-w-[85%] space-y-1.5 rounded-xl border border-white/25 bg-card/55 p-3 text-card-foreground shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-card/45">
        <div className="text-[13px]">
          <FormatCopilotText text={streamed} streaming={showCaret} />
          {showCaret && <span className="copilot-stream-caret" aria-hidden />}
        </div>

        {message.appointments && message.appointments.length > 0 && !message.stream && (
          <AppointmentList appointments={message.appointments} />
        )}
        {message.appointments && message.appointments.length > 0 && message.stream && streamed === message.text && (
          <div className="copilot-msg-in">
            <AppointmentList appointments={message.appointments} />
          </div>
        )}

        <span className="block text-right text-[9px] opacity-70">{message.timestamp}</span>
      </div>
    </div>
  );
}

function AppointmentList({
  appointments,
}: {
  appointments: NonNullable<CopilotChatMessage["appointments"]>;
}) {
  return (
    <div className="mt-2 space-y-1 border-t border-border/40 pt-1.5 text-[11px]">
      <span className="flex items-center gap-1 font-bold text-primary">
        <Calendar className="h-3 w-3" />
        Today&apos;s schedule
      </span>
      {appointments.map((a, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/40 p-1.5"
        >
          <span className="font-medium">
            {a.time} - {a.patient_name}
          </span>
          <Badge variant="outline" className="text-[9px]">
            {a.status}
          </Badge>
        </div>
      ))}
    </div>
  );
}

export function CopilotChatMessages({
  messages,
  loading,
  className,
  onStreamComplete,
}: {
  messages: CopilotChatMessage[];
  loading: boolean;
  className?: string;
  onStreamComplete?: (id: string) => void;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  return (
    <div className={cn("space-y-3 text-xs", className)}>
      {messages.map((m) =>
        m.sender === "user" ? (
          <div key={m.id} className="copilot-msg-in flex justify-end gap-2">
            <div className="max-w-[85%] space-y-1.5 rounded-xl bg-primary/90 p-3 text-primary-foreground shadow-sm backdrop-blur-sm">
              <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
              <span className="block text-right text-[9px] opacity-70">{m.timestamp}</span>
            </div>
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <User className="h-3.5 w-3.5" />
            </div>
          </div>
        ) : (
          <CopilotReplyBubble
            key={m.id}
            message={m}
            onStreamComplete={onStreamComplete}
            onContentUpdate={scrollToBottom}
          />
        ),
      )}

      {loading && <TypingIndicator />}
      <div ref={bottomRef} className="h-px shrink-0" aria-hidden />
    </div>
  );
}
